// react
import { useState } from 'react';

// router
import { Link as RouterLink } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Project, Loan } from '../../../@types/index';
import { flagCodes } from '../../../@types/unions';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// material
import { Box, Button, Grid, Paper, Typography } from '@mui/material';

// links
import { LinkProject } from '../../links/LinkProject/LinkProject';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { ChipUtilization } from '../../ui/ChipUtilization/ChipUtilization';
import { Flag } from '../../ui/Flag/Flag';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { IconCircleLoan } from '../../ui/IconCircleLoan/IconCircleLoan';
import { List } from '../../ui/List/List';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Table, TableHeader } from '../../ui/Table/Table';
import { Time } from '../../ui/Time/Time';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface CardProjectProps {
	isOnboarded?: boolean;
	isMock?: boolean;
	project: Project;
}

export const CardProject = ({ isOnboarded = true, isMock, project }: CardProjectProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [isExpanded, setIsExpanded] = useState(false);
	const [projectCalculated, setProjectCalculated] = useState<null | Project>(null);

	// fetch calcuations for project
	const { isLoading: isLoadingProjectCalculated } = useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/projects/${project.id}?fields=id,traceability,utilization`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setProjectCalculated(res.data[0]);
			}
		},
	});

	// Adjust column widths based on feature flag
	// When feature is disabled, we have 4 columns and need to adjust the widths accordingly
	const columnOverviewWidth = 3;
	const columnIdWidth = 1;
	const columnCountryWidth = 1.5;
	const columnFinancingWidth = 1.5;
	const columnUtilizationWidth = featureFlags['FINANCIAL_TRACEABILITY'] ? 1 : 1.25;
	const columnTxTraceabilityWidth = featureFlags['FINANCIAL_TRACEABILITY'] ? 1 : 1.25;

	const headers: TableHeader[] = [
		{
			label: t('financing'),
			value: 'code',
			render: (code: string, header: TableHeader, loan: Loan) => {
				return (
					<Grid container spacing={1}>
						<Grid item>
							<IconCircleLoan loan={loan} size="xs" />
						</Grid>
						<Grid item>
							<Typography fontWeight={600}>{code}</Typography>
						</Grid>
					</Grid>
				);
			},
		},
		{
			label: t('currency'),
			value: 'currency',
		},
		{
			align: 'right',
			label: t('amount'),
			value: 'amount',
			render: (amount: number, header: TableHeader, item: Loan) => {
				return <Amount currency={item.currency} value={amount} />;
			},
		},
		{
			label: t('closing'),
			value: 'dateClosing',
			render: (dateClosing: Date) => {
				return <Time date={new Date(dateClosing)} />;
			},
		},
	];

	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	let urlProject = `/project/${project.idWbg}?isWbg=true`;

	if (isMock) {
		urlProject += `&isMock=true`;
	}

	return (
		<Paper>
			<Box
				sx={(theme) => {
					return {
						position: 'relative',
						padding: theme.spacing(2),
					};
				}}>
				<Grid alignItems="center" container spacing={2}>
					<Grid item xs={isOnboarded ? 12 : 8} lg={columnOverviewWidth}>
						<Grid alignItems="center" container spacing={2} wrap="nowrap">
							<Grid item>
								<IconCircle type="project" />
							</Grid>
							<Grid item>
								<Box component="span" sx={{ display: 'inline-flex' }}>
									{isOnboarded ? (
										<LinkProject project={project} />
									) : (
										<Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>
											{project.name}
										</Typography>
									)}
								</Box>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} lg={columnIdWidth}>
						<RowKeyValue
							label={t('id')}
							value={
								<Typography color="brand.purple.500" fontWeight={600}>
									{project.idWbg}
								</Typography>
							}
						/>
					</Grid>
					<Grid item xs={12} lg={columnCountryWidth}>
						<RowKeyValue
							label={t('country')}
							value={
								<Grid
									alignItems="center"
									container
									justifyContent={{ xs: 'flex-end', lg: ' flex-start' }}
									spacing={1}>
									{flagCodes.includes(project.country.code) && (
										<Grid item>
											<Flag code={project.country.code} />
										</Grid>
									)}
									<Grid flex={{ lg: 1 }} item>
										<Typography>{project.country.name}</Typography>
									</Grid>
								</Grid>
							}
						/>
					</Grid>

					<Grid item xs={12} lg={columnFinancingWidth}>
						<RowKeyValue
							label={t('financing')}
							value={
								<List
									items={project.loans.map((loan) => loan.code)}
									maxItems={2}
									TypographyProps={{ color: 'brand.grey.500', fontWeight: 600, variant: 'caption' }}
								/>
							}
						/>
					</Grid>
					{isOnboarded && featureFlags['UTILIZATION'] && (
						<Grid item xs={12} lg={columnUtilizationWidth}>
							<RowKeyValue
								label={t('utilization')}
								value={
									<ChipUtilization
										isLoading={isLoadingProjectCalculated}
										utilization={projectCalculated?.utilization}
									/>
								}
							/>
						</Grid>
					)}
					{isOnboarded && (
						<>
							<Grid item xs={12} lg={columnTxTraceabilityWidth}>
								<RowKeyValue
									label={t('transactionsEvidenced')}
									value={
										<ChipTraceability
											isLoading={isLoadingProjectCalculated}
											traceability={projectCalculated?.traceability}
											type="TRANSACTIONS"
										/>
									}
								/>
							</Grid>
							{featureFlags['FINANCIAL_TRACEABILITY'] && (
								<Grid item xs={12} lg={1}>
									<RowKeyValue
										label={t('financialValueEvidenced')}
										value={
											<ChipTraceability
												isLoading={isLoadingProjectCalculated}
												traceability={projectCalculated?.traceability}
												type="FINANCIAL"
											/>
										}
									/>
								</Grid>
							)}
							<Grid item xs={12} lg={1.5}>
								<RowKeyValue
									label={t('updated')}
									value={
										<UserAvatarModified
											activityTypes={['PROJECT_UPDATED', 'PROJECT_CREATED']}
											project={project}
										/>
									}
								/>
							</Grid>
						</>
					)}
					<Grid item xs={12} lg={true}>
						<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={2}>
							{!isOnboarded && (
								<Grid
									item
									sx={(theme) => {
										return {
											position: 'absolute',
											top: 0,
											right: theme.spacing(2),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										};
									}}>
									<Button component={RouterLink} to={urlProject} variant="outlined">
										{t('onboard')}
									</Button>
								</Grid>
							)}

							<Grid item>
								<BtnExpand
									isExpanded={isExpanded}
									label={isExpanded ? t('hideLoans') : t('seeLoans')}
									onClick={handleToggleCard}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
			{isExpanded && (
				<div>
					<Table data={project.loans} headers={headers} />
				</div>
			)}
		</Paper>
	);
};
